
export type BallCollisionSoundType = 'MELODY' | 'SOUND_EFFECT' | 'CUSTOM' | 'SONG_SLICE' | 'NONE';
export type BallSoundEffect = string; 
export type MelodyName = string;

export type MelodyInstrument = 
  'GRAND_PIANO' | 'BRIGHT_PIANO' | 'ELECTRIC_PIANO' | 'HONKY_TONK' | 'HARPSICHORD' | 'CLAVINET' |
  'CELESTA' | 'GLOCKENSPIEL' | 'MUSIC_BOX' | 'VIBRAPHONE' | 'MARIMBA' | 'XYLOPHONE' | 'TUBULAR_BELLS' | 'DULCIMER' |
  'DRAWBAR_ORGAN' | 'PERCUSSIVE_ORGAN' | 'ROCK_ORGAN' | 'CHURCH_ORGAN' | 'REED_ORGAN' | 'ACCORDION' | 'HARMONICA' | 'TANGO_ACCORDION' |
  'NYLON_GUITAR' | 'STEEL_GUITAR' | 'JAZZ_GUITAR' | 'CLEAN_GUITAR' | 'MUTED_GUITAR' | 'OVERDRIVEN_GUITAR' | 'DISTORTION_GUITAR' | 'GUITAR_HARMONICS' |
  'ACOUSTIC_BASS' | 'FINGER_BASS' | 'PICK_BASS' | 'FRETLESS_BASS' | 'SLAP_BASS_1' | 'SLAP_BASS_2' | 'SYNTH_BASS_1' | 'SYNTH_BASS_2' | 'SYNTH_BASS_3' | 'SYNTH_BASS_4' |
  'VIOLIN' | 'VIOLA' | 'CELLO' | 'CONTRABASS' | 'TREMOLO_STRINGS' | 'PIZZICATO_STRINGS' | 'ORCHESTRAL_HARP' | 'TIMPANI' |
  'STRING_ENSEMBLE_1' | 'STRING_ENSEMBLE_2' | 'SYNTH_STRINGS_1' | 'SYNTH_STRINGS_2' | 'CHOIR_AAHS' | 'VOICE_OOHS' | 'SYNTH_VOICE' | 'ORCHESTRA_HIT' | 'ORCHESTRA_HIT_2' | 'ORCHESTRA_HIT_3' |
  'TRUMPET' | 'TROMBONE' | 'TUBA' | 'MUTED_TRUMPET' | 'FRENCH_HORN' | 'BRASS_SECTION' | 'SYNTH_BRASS_1' | 'SYNTH_BRASS_2' |
  'SOPRANO_SAX' | 'ALTO_SAX' | 'TENOR_SAX' | 'BARITONE_SAX' | 'OBOE' | 'ENGLISH_HORN' | 'BASSOON' | 'CLARINET' |
  'PICCOLO' | 'FLUTE' | 'RECORDER' | 'PAN_FLUTE' | 'BLOWN_BOTTLE' | 'SHAKUHACHI' | 'WHISTLE' | 'OCARINA' |
  'SQUARE_LEAD' | 'SAWTOOTH_LEAD' | 'CALLIOPE_LEAD' | 'CHIFF_LEAD' | 'CHARANG_LEAD' | 'VOICE_LEAD' | 'FIFTHS_LEAD' | 'BASS_LEAD' | '8BIT_LEAD' | 'SYNTH_PLUCK' | 'SYNTH_LEAD_3' | 'SYNTH_LEAD_4' |
  'NEW_AGE_PAD' | 'WARM_PAD' | 'POLYSYNTH_PAD' | 'CHOIR_PAD' | 'BOWED_PAD' | 'METALLIC_PAD' | 'HALO_PAD' | 'SWEEP_PAD' | 'SPACE_VOICE' | 'ALIEN_DRONE' |
  'RAIN_FX' | 'SOUNDTRACK_FX' | 'CRYSTAL_FX' | 'ATMOSPHERE_FX' | 'BRIGHTNESS_FX' | 'GOBLINS_FX' | 'ECHOES_FX' | 'SCI_FI_FX' | 'ETHEREAL_CHIME' | 'THUNDER_FX' | 'WIND_FX' | 'WATER_FX' | 'BIRD_TWEET' | 'TELEPHONE_RING' | 'HELICOPTER' | 'APPLAUSE' | 'GUNSHOT' |
  'SITAR' | 'BANJO' | 'SHAMISEN' | 'KOTO' | 'KALIMBA' | 'BAGPIPE' | 'FIDDLE' | 'SHANAI' |
  'TINKLE_BELL' | 'AGOGO' | 'STEEL_DRUMS' | 'WOODBLOCK' | 'TAIKO_DRUM' | 'MELODIC_TOM' | 'SYNTH_DRUM' | 'REVERSE_CYMBAL' | 'PURE_SINE' | 'CHURCH_BELL' | 'HAND_BELL';

export enum MidiNoteAction {
  NONE = 0,
  PLAY = 1,
  BOUNCE = 2
}

export interface RhythmicNote {
  notes: number | number[];
  duration?: number;
}

export interface MidiNote {
  name: string;
  midi: number;
  time: number;
  duration: number;
  velocity: number;
  track?: number;
  channel?: number;
}

export interface MidiData {
  notes: MidiNote[];
  duration: number;
  header: {
    name: string;
    ppq: number;
    tempos: { bpm: number; time: number }[];
    timeSignatures: { timeSignature: number[]; time: number }[];
    tracks?: { index: number; name: string }[];
  };
}

export type MelodyData = (number | number[] | RhythmicNote)[];

export interface CustomMelody {
  id: string;
  name: string;
  frequencies: MelodyData;
  midiData?: MidiData;
  format: 'FREQUENCIES' | 'MIDI';
  url?: string;
}

export interface UserSound {
  id: string;
  name: string;
  base64?: string;
  url?: string;
}

export interface BreakSoundZone {
  id: string;
  threshold: number;
  soundType: BallCollisionSoundType;
  soundEffect: BallSoundEffect;
  customSoundId?: string;
}

export interface SongSlice {
  start: number;
  duration: number;
}

export interface BreakSoundZonePreset {
  id: string;
  name: string;
  zones: BreakSoundZone[];
}

export interface AudioPreset {
  name: string;
  category: string;
  instrument?: string;
  echoType?: 'NONE' | 'STEREO' | 'MONO' | 'LONG' | 'SHORT';
  clarity?: 'CLEAR' | 'MUFFLED' | 'DISTORTED' | 'AMBIENT';
  tags?: string[];
  settings: any; 
}
